import { Link } from 'react-router-dom';
import { TrustpilotWidget } from '@/components/Trustpilot';
import { HardHat, ArrowRight, CheckCircle2 } from 'lucide-react';
import Seo from '@/components/Seo';

export default function AboutPage() {
  return (
    <div className="bg-white" data-testid="about-page">
      <Seo
        title="About TradesMate UK"
        description="TradesMate UK is built for UK tradesmen — free trade calculators, fast estimating, branded quotes and invoices, and live job profit tracking. Quote smarter, win more work."
        path="/about"
      />
      {/* Hero */}
      <section className="border-b-2 border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-xs uppercase tracking-widest bg-slate-900 text-white px-2 py-1 font-semibold">V1.0 · UK</span>
            <span className="text-xs uppercase tracking-widest border border-slate-200 px-2 py-1 font-semibold">About</span>
          </div>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-[0.95]">
            Welcome to <em className="italic text-brand">TradesMate</em> UK
          </h1>
          <p className="mt-6 font-display font-bold text-base sm:text-lg uppercase tracking-tight text-brand">
            // Built by a tradesman, for tradesmen.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <article className="prose-tm space-y-6 text-slate-800 text-lg leading-relaxed">
          <p>
            My name is <strong>Tyler</strong>, and after years of running a successful groundworks
            and paving business, I discovered that one thing consistently slowed me down more than
            anything else: <strong>quoting jobs.</strong>
          </p>

          <p>
            Every estimate seemed to involve multiple calculators, spreadsheets, notebooks, supplier
            price lists, and endless double-checking. What should have taken minutes often took hours.
            Like many tradesmen, I found myself spending evenings and weekends creating professional
            quotes instead of focusing on running my business and spending quality time with family
            and friends. Instead, I was constantly buried in paperwork.
          </p>

          <p>
            The biggest frustration wasn&apos;t even the time spent creating quotes &mdash; it was the
            <em> uncertainty afterwards</em>. Did the customer receive it? Have they opened it? Are
            they still interested? Or had I just spent five hours preparing a detailed estimate that
            would never even be looked at?
          </p>

          <div className="my-10 border-l-2 border-brand pl-6 py-2 bg-orange-50">
            <p className="font-display font-semibold text-2xl sm:text-3xl tracking-tight uppercase text-slate-900 m-0">
              That&apos;s why I decided to build my own solution.
            </p>
          </div>

          <p>
            <strong>TradesMateUK</strong> brings together all the calculators, estimating tools,
            pricing systems, and quote management features tradespeople need in one place. Whether
            you&apos;re a groundworker, builder, landscaper, electrician, plumber, roofer, decorator,
            or any other trade professional, you can create accurate, professional estimates faster
            and more efficiently than ever before.
          </p>

          <p>
            For those who want even greater control, our <strong className="text-brand">PRO+</strong>{' '}
            version takes things a step further. Send quotes directly to customers and receive instant
            notifications when they&apos;re opened. You&apos;ll know exactly when a quote has been viewed,
            accepted, or declined &mdash; removing the guesswork and helping you follow up at the right time.
          </p>

          <div className="my-10 grid sm:grid-cols-2 gap-3">
            {[
              'Save hours per quote',
              'Improve productivity',
              'Look professional every time',
              'Win more work',
            ].map((g) => (
              <div key={g} className="flex items-start gap-3 p-4 border border-slate-200 bg-white">
                <CheckCircle2 className="text-brand shrink-0 mt-0.5" size={22}/>
                <span className="font-display font-bold uppercase tracking-tight">{g}</span>
              </div>
            ))}
          </div>

          <p>
            Our goal is simple: to save tradespeople time, improve productivity, create professional
            estimates, and help win more work without compromising on quality.
          </p>
        </article>

        {/* Sign-off block */}
        <div className="mt-12 p-6 sm:p-8 bg-slate-900 text-white">
          <p className="font-display font-semibold text-2xl sm:text-3xl tracking-tight uppercase leading-tight">
            Built by a Tradesman, for Tradesmen.
          </p>
          <p className="mt-2 text-xs uppercase tracking-widest text-brand font-semibold">
            ~ <HardHat size={14} className="inline mb-1 mr-1"/>TradesMate<span className="text-white">UK</span>
          </p>
        </div>

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="about-cta-row">
          <Link to="/register" data-testid="about-cta-signup" className="tm-btn">
            Get started free <ArrowRight size={18}/>
          </Link>
          <Link to="/trades" className="tm-btn tm-btn-ghost">Browse calculators</Link>
        </div>
        <div className="mt-6 max-w-md" data-testid="about-trustpilot">
          <TrustpilotWidget />
        </div>
      </section>
    </div>
  );
}
